import {FC, useState, useEffect, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  getRecordings,
  createRecording,
  deleteRecording,
  updateRecording,
  getStats,
  SalesRecording,
  StatsResponse,
} from './core/_requests'

const COMMON_TAGS = [
  'closing',
  'objection handling',
  'cold call',
  'follow-up',
  'intro',
  'demo',
  'negotiation',
  'upsell',
]

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatDuration(seconds: number): string {
  if (!seconds) return '--:--'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('he-IL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

const SalesLibraryPage: FC = () => {
  const navigate = useNavigate()
  const [recordings, setRecordings] = useState<SalesRecording[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<StatsResponse | null>(null)

  // Filters
  const [search, setSearch] = useState('')
  const [filterTag, setFilterTag] = useState('')
  const [filterRating, setFilterRating] = useState('')
  const [filterPublished, setFilterPublished] = useState('')

  // Upload modal
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDesc, setUploadDesc] = useState('')
  const [uploadSalesperson, setUploadSalesperson] = useState('')
  const [uploadTags, setUploadTags] = useState<string[]>([])
  const [uploadRating, setUploadRating] = useState<number | ''>('')
  const [uploadPublished, setUploadPublished] = useState(false)
  const [customTag, setCustomTag] = useState('')

  // Currently playing audio
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const searchTimeout = useRef<NodeJS.Timeout>()

  const fetchRecordings = async () => {
    setLoading(true)
    try {
      const data = await getRecordings({
        page,
        limit: 20,
        search,
        tag: filterTag,
        rating: filterRating,
        is_published: filterPublished,
      })
      setRecordings(data.recordings)
      setTotal(data.total)
      setTotalPages(data.totalPages)
    } catch (err) {
      console.error('Failed to fetch recordings:', err)
    }
    setLoading(false)
  }

  const fetchStats = async () => {
    try {
      const data = await getStats()
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }

  useEffect(() => {
    fetchRecordings()
  }, [page, filterTag, filterRating, filterPublished])

  useEffect(() => {
    fetchStats()
  }, [])

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      setPage(1)
      fetchRecordings()
    }, 400)
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current)
    }
  }, [search])

  const handleUpload = async () => {
    if (!uploadFile) return
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', uploadFile)
      formData.append('title', uploadTitle || uploadFile.name)
      formData.append('description', uploadDesc)
      formData.append('salesperson', uploadSalesperson)
      formData.append('tags', JSON.stringify(uploadTags))
      if (uploadRating) formData.append('rating', String(uploadRating))
      formData.append('is_published', String(uploadPublished))

      await createRecording(formData)
      setShowUpload(false)
      resetUploadForm()
      fetchRecordings()
      fetchStats()
    } catch (err) {
      alert('Failed to upload recording')
      console.error(err)
    }
    setUploading(false)
  }

  const resetUploadForm = () => {
    setUploadFile(null)
    setUploadTitle('')
    setUploadDesc('')
    setUploadSalesperson('')
    setUploadTags([])
    setUploadRating('')
    setUploadPublished(false)
    setCustomTag('')
  }

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Delete "${title}"?`)) return
    try {
      await deleteRecording(id)
      fetchRecordings()
      fetchStats()
    } catch (err) {
      alert('Failed to delete recording')
    }
  }

  const handleTogglePublished = async (rec: SalesRecording) => {
    try {
      await updateRecording(rec._id, {is_published: !rec.is_published})
      fetchRecordings()
      fetchStats()
    } catch (err) {
      alert('Failed to update recording')
    }
  }

  const togglePlay = (rec: SalesRecording) => {
    if (playingId === rec._id) {
      audioRef.current?.pause()
      setPlayingId(null)
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
      }
      const audio = new Audio(rec.file_url)
      audio.play()
      audio.onended = () => setPlayingId(null)
      audioRef.current = audio
      setPlayingId(rec._id)
    }
  }

  const toggleUploadTag = (tag: string) => {
    setUploadTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  const addCustomTag = () => {
    const tag = customTag.trim()
    if (tag && !uploadTags.includes(tag)) {
      setUploadTags((prev) => [...prev, tag])
    }
    setCustomTag('')
  }

  // Collect all unique tags from stats
  const allTags = stats?.tagCounts?.map((t) => t._id) || []

  return (
    <div style={{padding: '24px', direction: 'rtl'}}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div>
          <h2 style={{margin: 0, fontSize: '24px', fontWeight: 700}}>
            <i className='bi bi-mic' style={{marginLeft: '8px'}}></i>
            Sales Training Library
          </h2>
          <p style={{margin: '4px 0 0', color: '#5e6278', fontSize: '14px'}}>
            {total} recordings
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          style={{
            padding: '10px 20px',
            background: '#3699ff',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <i className='bi bi-plus-lg'></i>
          Upload Recording
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{fontSize: '28px', fontWeight: 700, color: '#1e1e2d'}}>
              {stats.total}
            </div>
            <div style={{fontSize: '13px', color: '#5e6278'}}>Total Recordings</div>
          </div>
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{fontSize: '28px', fontWeight: 700, color: '#1bc5bd'}}>
              {stats.published}
            </div>
            <div style={{fontSize: '13px', color: '#5e6278'}}>Published</div>
          </div>
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{fontSize: '28px', fontWeight: 700, color: '#ffa800'}}>
              {stats.drafts}
            </div>
            <div style={{fontSize: '13px', color: '#5e6278'}}>Drafts</div>
          </div>
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{fontSize: '28px', fontWeight: 700, color: '#8950fc'}}>
              {stats.avgRating > 0 ? stats.avgRating : '--'}
            </div>
            <div style={{fontSize: '13px', color: '#5e6278'}}>Avg Rating</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          type='text'
          placeholder='Search by title, description...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '8px 14px',
            border: '1px solid #e8ecef',
            borderRadius: '8px',
            fontSize: '14px',
            width: '260px',
            background: '#fff',
          }}
        />
        <select
          value={filterTag}
          onChange={(e) => {
            setFilterTag(e.target.value)
            setPage(1)
          }}
          style={{
            padding: '8px 14px',
            border: '1px solid #e8ecef',
            borderRadius: '8px',
            fontSize: '14px',
            background: '#fff',
          }}
        >
          <option value=''>All Tags</option>
          {allTags.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </select>
        <select
          value={filterRating}
          onChange={(e) => {
            setFilterRating(e.target.value)
            setPage(1)
          }}
          style={{
            padding: '8px 14px',
            border: '1px solid #e8ecef',
            borderRadius: '8px',
            fontSize: '14px',
            background: '#fff',
          }}
        >
          <option value=''>All Ratings</option>
          {[5, 4, 3, 2, 1].map((r) => (
            <option key={r} value={r}>
              {'★'.repeat(r)}{'☆'.repeat(5 - r)}
            </option>
          ))}
        </select>
        <select
          value={filterPublished}
          onChange={(e) => {
            setFilterPublished(e.target.value)
            setPage(1)
          }}
          style={{
            padding: '8px 14px',
            border: '1px solid #e8ecef',
            borderRadius: '8px',
            fontSize: '14px',
            background: '#fff',
          }}
        >
          <option value=''>All Status</option>
          <option value='true'>Published</option>
          <option value='false'>Draft</option>
        </select>
      </div>

      {/* Recordings List */}
      {loading ? (
        <div style={{textAlign: 'center', padding: '60px 0', color: '#5e6278'}}>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : recordings.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 0',
            color: '#5e6278',
            background: '#fff',
            borderRadius: '12px',
          }}
        >
          <i
            className='bi bi-mic-mute'
            style={{fontSize: '48px', display: 'block', marginBottom: '12px', opacity: 0.3}}
          ></i>
          <p>No recordings found</p>
        </div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          {recordings.map((rec) => (
            <div
              key={rec._id}
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '16px 20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              {/* Play button */}
              <button
                onClick={() => togglePlay(rec)}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  border: 'none',
                  background: playingId === rec._id ? '#f64e60' : '#3699ff',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  flexShrink: 0,
                }}
              >
                <i className={`bi bi-${playingId === rec._id ? 'pause-fill' : 'play-fill'}`}></i>
              </button>

              {/* Info */}
              <div style={{flex: 1, minWidth: 0}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                  <span
                    onClick={() => navigate(`/admin/sales-library/${rec._id}`)}
                    style={{
                      fontWeight: 600,
                      fontSize: '15px',
                      cursor: 'pointer',
                      color: '#1e1e2d',
                    }}
                  >
                    {rec.title}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: rec.is_published ? '#e8fff3' : '#fff8e1',
                      color: rec.is_published ? '#1bc5bd' : '#ffa800',
                      fontWeight: 600,
                    }}
                  >
                    {rec.is_published ? 'Published' : 'Draft'}
                  </span>
                  {rec.ai_status === 'completed' && (
                    <span
                      title='AI Analyzed'
                      style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: '#f3efff',
                        color: '#8950fc',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                      }}
                    >
                      <i className='bi bi-robot' style={{fontSize: '10px'}}></i>
                      AI
                    </span>
                  )}
                  {rec.ai_status === 'processing' && (
                    <span
                      title='AI Processing...'
                      style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: '#e3f2fd',
                        color: '#3699ff',
                        fontWeight: 600,
                      }}
                    >
                      <div className='spinner-border spinner-border-sm' style={{width: '10px', height: '10px', borderWidth: '2px'}} role='status'></div>
                    </span>
                  )}
                </div>

                <div style={{display: 'flex', gap: '12px', fontSize: '13px', color: '#5e6278', flexWrap: 'wrap'}}>
                  {rec.salesperson && (
                    <span>
                      <i className='bi bi-person' style={{marginLeft: '4px'}}></i>
                      {rec.salesperson}
                    </span>
                  )}
                  <span>
                    <i className='bi bi-calendar3' style={{marginLeft: '4px'}}></i>
                    {formatDate(rec.created_at)}
                  </span>
                  <span>{formatFileSize(rec.file_size)}</span>
                  {rec.duration > 0 && <span>{formatDuration(rec.duration)}</span>}
                  {rec.rating && (
                    <span style={{color: '#ffa800'}}>
                      {'★'.repeat(rec.rating)}
                      {'☆'.repeat(5 - rec.rating)}
                    </span>
                  )}
                </div>

                {rec.tags.length > 0 && (
                  <div style={{display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap'}}>
                    {rec.tags.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: '#f0f0f5',
                          color: '#5e6278',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{display: 'flex', gap: '8px', flexShrink: 0}}>
                <button
                  onClick={() => handleTogglePublished(rec)}
                  title={rec.is_published ? 'Unpublish' : 'Publish'}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #e8ecef',
                    borderRadius: '6px',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: rec.is_published ? '#1bc5bd' : '#a1a5b7',
                  }}
                >
                  <i className={`bi bi-${rec.is_published ? 'eye-fill' : 'eye-slash'}`}></i>
                </button>
                <button
                  onClick={() => navigate(`/admin/sales-library/${rec._id}`)}
                  title='Edit'
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #e8ecef',
                    borderRadius: '6px',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#3699ff',
                  }}
                >
                  <i className='bi bi-pencil'></i>
                </button>
                <button
                  onClick={() => handleDelete(rec._id, rec.title)}
                  title='Delete'
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #e8ecef',
                    borderRadius: '6px',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#f64e60',
                  }}
                >
                  <i className='bi bi-trash'></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '24px',
          }}
        >
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            style={{
              padding: '8px 14px',
              border: '1px solid #e8ecef',
              borderRadius: '8px',
              background: '#fff',
              cursor: page === 1 ? 'default' : 'pointer',
              opacity: page === 1 ? 0.5 : 1,
            }}
          >
            <i className='bi bi-chevron-right'></i>
          </button>
          <span
            style={{
              padding: '8px 14px',
              fontSize: '14px',
              color: '#5e6278',
            }}
          >
            {page} / {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            style={{
              padding: '8px 14px',
              border: '1px solid #e8ecef',
              borderRadius: '8px',
              background: '#fff',
              cursor: page === totalPages ? 'default' : 'pointer',
              opacity: page === totalPages ? 0.5 : 1,
            }}
          >
            <i className='bi bi-chevron-left'></i>
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <>
          <div
            onClick={() => {
              setShowUpload(false)
              resetUploadForm()
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 1000,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#fff',
              borderRadius: '16px',
              padding: '28px',
              zIndex: 1001,
              width: '520px',
              maxHeight: '90vh',
              overflowY: 'auto',
              direction: 'rtl',
            }}
          >
            <h3 style={{margin: '0 0 20px', fontSize: '20px', fontWeight: 700}}>
              Upload Recording
            </h3>

            {/* File picker */}
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
                Audio File *
              </label>
              <input
                type='file'
                accept='audio/*'
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    setUploadFile(file)
                    if (!uploadTitle) setUploadTitle(file.name.replace(/\.[^.]+$/, ''))
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e8ecef',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
              {uploadFile && (
                <div style={{fontSize: '12px', color: '#5e6278', marginTop: '4px'}}>
                  {formatFileSize(uploadFile.size)}
                </div>
              )}
            </div>

            {/* Title */}
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
                Title *
              </label>
              <input
                type='text'
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder='Recording title'
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e8ecef',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Description */}
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
                Description
              </label>
              <textarea
                value={uploadDesc}
                onChange={(e) => setUploadDesc(e.target.value)}
                placeholder='What can be learned from this call?'
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e8ecef',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Salesperson */}
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
                Salesperson
              </label>
              <input
                type='text'
                value={uploadSalesperson}
                onChange={(e) => setUploadSalesperson(e.target.value)}
                placeholder='Who made this call?'
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e8ecef',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Tags */}
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
                Tags
              </label>
              <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px'}}>
                {COMMON_TAGS.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleUploadTag(tag)}
                    style={{
                      padding: '4px 10px',
                      border: '1px solid',
                      borderColor: uploadTags.includes(tag) ? '#3699ff' : '#e8ecef',
                      borderRadius: '16px',
                      background: uploadTags.includes(tag) ? '#e3f2fd' : '#fff',
                      color: uploadTags.includes(tag) ? '#3699ff' : '#5e6278',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div style={{display: 'flex', gap: '8px'}}>
                <input
                  type='text'
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addCustomTag()
                    }
                  }}
                  placeholder='Custom tag...'
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: '1px solid #e8ecef',
                    borderRadius: '8px',
                    fontSize: '13px',
                  }}
                />
                <button
                  onClick={addCustomTag}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e8ecef',
                    borderRadius: '8px',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Add
                </button>
              </div>
              {uploadTags.filter((t) => !COMMON_TAGS.includes(t)).length > 0 && (
                <div style={{display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap'}}>
                  {uploadTags
                    .filter((t) => !COMMON_TAGS.includes(t))
                    .map((tag) => (
                      <span
                        key={tag}
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          background: '#e3f2fd',
                          color: '#3699ff',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {tag}
                        <i
                          className='bi bi-x'
                          style={{cursor: 'pointer'}}
                          onClick={() => toggleUploadTag(tag)}
                        ></i>
                      </span>
                    ))}
                </div>
              )}
            </div>

            {/* Rating */}
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
                Rating
              </label>
              <div style={{display: 'flex', gap: '4px'}}>
                {[1, 2, 3, 4, 5].map((r) => (
                  <button
                    key={r}
                    onClick={() => setUploadRating(uploadRating === r ? '' : r)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '22px',
                      color: r <= (uploadRating || 0) ? '#ffa800' : '#e8ecef',
                      padding: '0 2px',
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            {/* Published toggle */}
            <div style={{marginBottom: '24px'}}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                <input
                  type='checkbox'
                  checked={uploadPublished}
                  onChange={(e) => setUploadPublished(e.target.checked)}
                  style={{width: '16px', height: '16px'}}
                />
                Publish immediately
              </label>
            </div>

            {/* Actions */}
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-start'}}>
              <button
                onClick={handleUpload}
                disabled={!uploadFile || !uploadTitle || uploading}
                style={{
                  padding: '10px 24px',
                  background: !uploadFile || !uploadTitle || uploading ? '#a1a5b7' : '#3699ff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: !uploadFile || !uploadTitle || uploading ? 'default' : 'pointer',
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => {
                  setShowUpload(false)
                  resetUploadForm()
                }}
                style={{
                  padding: '10px 24px',
                  background: '#fff',
                  color: '#5e6278',
                  border: '1px solid #e8ecef',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default SalesLibraryPage
